import React, { useState } from 'react';
import {
    Globe,
    Image as ImageIcon,
    Info,
    Move,
} from 'lucide-react';
import './Identity.css';

// Preset avatar options
const BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';
const PRESET_AVATARS = [
    '/assets/Avatars/AV29.png',
    '/assets/Avatars/AV45.png',
    '/assets/Avatars/AV20.png',
    '/assets/Avatars/AV5.png',
    '/assets/Avatars/AV38.png',
    '/assets/Avatars/AV60.png',
    '/assets/Avatars/AV87.png',
    '/assets/Avatars/AV90.png',
    '/assets/Avatars/AV95.png',
    '/assets/ChatbotToggleButtons/Adriana O\'Sullivan.webp',
    '/assets/ChatbotToggleButtons/Aliah Lane.webp',
    '/assets/ChatbotToggleButtons/Alisa Hester.webp',
    '/assets/ChatbotToggleButtons/Amanda Lowery.webp',
    '/assets/ChatbotToggleButtons/Ammar Foley.webp',
    '/assets/ChatbotToggleButtons/Ashton Blackwell.webp',
    '/assets/ChatbotToggleButtons/Ava Bentley.webp',
    '/assets/ChatbotToggleButtons/Bailey Richards.webp',
    '/assets/ChatbotToggleButtons/Benedict Doherty.webp',
    '/assets/ChatbotToggleButtons/Blake Riley.webp',
    '/assets/ChatbotToggleButtons/Elisa Nishikawa.webp',
    '/assets/ChatbotToggleButtons/Ethan Valdez.webp',
    '/assets/ChatbotToggleButtons/Jonathan Kelly.webp',
    '/assets/ChatbotToggleButtons/Kari Rasmussen.webp',
    '/assets/ChatbotToggleButtons/Marco Gross.webp',
    '/assets/ChatbotToggleButtons/Owen Harding.webp',
    '/assets/ChatbotToggleButtons/Kaden Scott.webp',
    '/assets/ChatbotToggleButtons/Zaid Schwartz.webp',
].map(url => url.startsWith('http') ? url : `${BASE_URL}${url}`);

/**
 * Identity Component
 * 
 * A consolidated configuration panel for the chatbot's core identity.
 * Replaces the old BasicSettings and parts of StyleSettings.
 * Designed with the "Inbox" aesthetic: clean, modern, and slate-themed.
 */
function Identity({
    // Profile Props
    assistantName,
    setAssistantName,
    botTagline,
    setBotTagline,

    // Visual Props
    botIconUrl,
    setBotIconUrl,
    botIconOffsetX = 0,
    botIconOffsetY = 0,

    // Behavior Props
    defaultLanguage,
    setDefaultLanguage,
    messagePrompt,
    setMessagePrompt,

    // Intro Props
    introMessageText,
    setIntroMessageText,
    customIntroMessage,
    setCustomIntroMessage,

    // Config Handlers
    updateConfig,
    saveConfig
}) {
    // Local state for avatar grid expand/collapse
    const [isAvatarGridExpanded, setIsAvatarGridExpanded] = useState(false);

    // Helper for language names
    const getLanguageName = (code) => {
        const langs = {
            'auto': 'Auto Detect',
            'en': 'English',
            'es': 'Spanish',
            'fr': 'French',
            'de': 'German',
            'zh': 'Chinese',
            'ar': 'Arabic',
            'hi': 'Hindi'
        };
        return langs[code] || code;
    };

    // Helper to handle updates (update state + optional save)
    const handleUpdate = (keyOrUpdates, value, shouldSave = false) => {
        // Support batch updates
        let updates = {};
        if (typeof keyOrUpdates === 'object') {
            updates = keyOrUpdates;
        } else {
            updates = { [keyOrUpdates]: value };
        }

        // Update parent state
        updateConfig(updates);

        // Update local set functions if available
        Object.entries(updates).forEach(([key, val]) => {
            switch (key) {
                case 'assistantName': setAssistantName(val); break;
                case 'botTagline': setBotTagline(val); break;
                case 'botIconUrl': setBotIconUrl(val); break;
                case 'defaultLanguage': setDefaultLanguage(val); break;
                case 'messagePrompt': setMessagePrompt(val); break;
                case 'introMessageText': setIntroMessageText(val); break;
                case 'customIntroMessage': setCustomIntroMessage(val); break;
                default: break;
            }
        });

        if (shouldSave && saveConfig) {
            saveConfig(updates);
        }
    };

    // Effective offset values (use props directly for display)
    const effectiveIconOffsetX = botIconOffsetX ?? 0;
    const effectiveIconOffsetY = botIconOffsetY ?? 0;

    return (
        <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
            <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <h2 className="text-lg font-bold tracking-tight text-slate-900">Identity</h2>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">Define who your AI assistant is.</p>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="max-w-3xl mx-auto px-8 py-8 flex flex-col gap-10">

                        {/* Avatar Section */}
                        <div className="flex items-start gap-8">
                            <div className="flex-1 space-y-4 pt-1 max-w-2xl">
                                <div className="space-y-1">
                                    <label className="block text-sm font-semibold text-slate-900">Avatar</label>
                                    <p className="text-xs text-slate-500">
                                        Choose a preset or enter a custom URL below.
                                    </p>
                                </div>

                                {/* Preset Avatar Grid */}
                                <div className="space-y-2">
                                    <div className="flex flex-wrap gap-2">
                                        {(isAvatarGridExpanded ? PRESET_AVATARS : PRESET_AVATARS.slice(0, 7)).map((url, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleUpdate('botIconUrl', url, true)}
                                                className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-all hover:scale-105 ${botIconUrl && url === (botIconUrl.startsWith('/') ? `${BASE_URL}${botIconUrl}` : botIconUrl)
                                                    ? 'border-slate-900 ring-2 ring-slate-900/20'
                                                    : 'border-slate-200 hover:border-slate-300'
                                                    }`}
                                            >
                                                <img
                                                    src={url}
                                                    alt={`Avatar ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                        {!isAvatarGridExpanded && PRESET_AVATARS.length > 7 && (
                                            <button
                                                onClick={() => setIsAvatarGridExpanded(true)}
                                                className="w-12 h-12 rounded-full border-2 border-dashed border-slate-300 hover:border-slate-400 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all hover:scale-105"
                                            >
                                                <span className="text-xs font-bold">+{PRESET_AVATARS.length - 7}</span>
                                            </button>
                                        )}
                                    </div>
                                    {isAvatarGridExpanded && (
                                        <button
                                            onClick={() => setIsAvatarGridExpanded(false)}
                                            className="text-xs text-slate-400 hover:text-slate-600 font-medium"
                                        >
                                            Show less
                                        </button>
                                    )}
                                </div>

                                {/* Divider */}
                                <div className="relative py-1">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className="w-full border-t border-slate-100"></div>
                                    </div>
                                    <div className="relative flex justify-center">
                                        <span className="bg-white px-2 text-[10px] font-bold tracking-wider text-slate-300 uppercase">Or custom URL</span>
                                    </div>
                                </div>

                                <div className="relative">
                                    <ImageIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        value={botIconUrl && PRESET_AVATARS.includes(botIconUrl.startsWith('/') ? `${BASE_URL}${botIconUrl}` : botIconUrl) ? '' : (botIconUrl || '')}
                                        onChange={(e) => handleUpdate('botIconUrl', e.target.value)}
                                        onBlur={(e) => handleUpdate('botIconUrl', e.target.value, true)}
                                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all placeholder:text-slate-400 text-slate-800"
                                        placeholder="https://example.com/avatar.png"
                                    />
                                </div>

                                {/* Avatar Position Control */}
                                {botIconUrl && (
                                    <div className="space-y-3 pt-2 max-w-xs">
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex items-center gap-2">
                                                <Move size={14} className="text-slate-400" />
                                                <label className="font-semibold text-slate-900">Avatar Position</label>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    handleUpdate({
                                                        botIconOffsetX: 0,
                                                        botIconOffsetY: 0
                                                    }, null, true);
                                                }}
                                                className="text-[10px] font-medium text-slate-500 hover:text-slate-900 bg-slate-100 px-2 py-1 rounded transition-colors"
                                            >
                                                Reset Center
                                            </button>
                                        </div>

                                        <div className="flex gap-4">
                                            {/* The Position Pad */}
                                            <div
                                                className="relative w-full h-32 bg-slate-50 rounded-xl border border-slate-200 cursor-crosshair overflow-hidden group select-none touch-none"
                                                onMouseDown={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    const handleMove = (clientX, clientY) => {
                                                        const x = clientX - rect.left;
                                                        const y = clientY - rect.top;

                                                        // Map 0 -> width to -50 -> 50
                                                        let offsetX = Math.round(((x / rect.width) * 100) - 50);
                                                        let offsetY = Math.round(((y / rect.height) * 100) - 50);

                                                        // Clamp values
                                                        offsetX = Math.max(-50, Math.min(50, offsetX));
                                                        offsetY = Math.max(-50, Math.min(50, offsetY));

                                                        handleUpdate({
                                                            botIconOffsetX: offsetX,
                                                            botIconOffsetY: offsetY
                                                        });
                                                    };

                                                    handleMove(e.clientX, e.clientY);

                                                    const onMouseMove = (moveEvent) => {
                                                        handleMove(moveEvent.clientX, moveEvent.clientY);
                                                    };

                                                    const onMouseUp = () => {
                                                        window.removeEventListener('mousemove', onMouseMove);
                                                        window.removeEventListener('mouseup', onMouseUp);
                                                    };

                                                    window.addEventListener('mousemove', onMouseMove);
                                                    window.addEventListener('mouseup', onMouseUp);
                                                }}
                                            >
                                                {/* Grid Lines */}
                                                <div className="absolute inset-0 pointer-events-none opacity-20"
                                                    style={{
                                                        backgroundImage: 'linear-gradient(to right, #94a3b8 1px, transparent 1px), linear-gradient(to bottom, #94a3b8 1px, transparent 1px)',
                                                        backgroundSize: '25% 25%',
                                                        backgroundPosition: 'center center'
                                                    }}
                                                />

                                                {/* Center Axes */}
                                                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300 transform -translate-x-1/2" />
                                                <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-300 transform -translate-y-1/2" />

                                                {/* Draggable Handle */}
                                                <div
                                                    className="absolute w-4 h-4 bg-slate-900 rounded-full shadow-lg border-2 border-white transform -translate-x-1/2 -translate-y-1/2 transition-transform duration-75 ease-out"
                                                    style={{
                                                        left: `${((parseInt(effectiveIconOffsetX || 0) + 50) / 100) * 100}%`,
                                                        top: `${((parseInt(effectiveIconOffsetY || 0) + 50) / 100) * 100}%`
                                                    }}
                                                >
                                                    <div className="absolute inset-0 bg-slate-900 rounded-full animate-ping opacity-0 group-active:opacity-20" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Numeric Readout */}
                                        <div className="flex gap-2">
                                            <div className="flex-1 bg-slate-50 border border-slate-100 rounded px-2 py-1.5 flex justify-between items-center">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase">X</span>
                                                <span className="text-xs font-mono text-slate-700">{effectiveIconOffsetX}px</span>
                                            </div>
                                            <div className="flex-1 bg-slate-50 border border-slate-100 rounded px-2 py-1.5 flex justify-between items-center">
                                                <span className="text-[10px] text-slate-400 font-bold uppercase">Y</span>
                                                <span className="text-xs font-mono text-slate-700">{effectiveIconOffsetY}px</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Identity Details Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-3xl">
                            <div className="flex flex-col h-full gap-1">
                                <label className="block text-sm font-semibold text-slate-900">
                                    Assistant Name
                                </label>
                                <p className="text-xs text-slate-500">
                                    Customize the name of your virtual assistant.
                                </p>
                                <input
                                    type="text"
                                    value={assistantName}
                                    onChange={(e) => handleUpdate('assistantName', e.target.value)}
                                    onBlur={(e) => handleUpdate('assistantName', e.target.value, true)}
                                    className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all placeholder:text-slate-400 text-slate-800 mt-auto"
                                    placeholder="e.g. Maya"
                                />
                            </div>

                            <div className="flex flex-col h-full gap-1">
                                <label className="block text-sm font-semibold text-slate-900">
                                    Tagline
                                </label>
                                <p className="text-xs text-slate-500">
                                    A short description to introduce your bot.
                                </p>
                                <input
                                    type="text"
                                    value={botTagline}
                                    onChange={(e) => handleUpdate('botTagline', e.target.value)}
                                    onBlur={(e) => handleUpdate('botTagline', e.target.value, true)}
                                    className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all placeholder:text-slate-400 text-slate-800 mt-auto"
                                    placeholder="e.g. Usually replies instantly"
                                />
                            </div>
                        </div>

                        {/* Greeting Section */}
                        <div className="flex flex-col gap-4 max-w-3xl pt-2">
                            <div className="flex items-center justify-between">
                                <div className="space-y-1">
                                    <label className="block text-sm font-semibold text-slate-900">
                                        Greeting Message
                                    </label>
                                    <p className="text-xs text-slate-500">
                                        The first message sent to visitors.
                                    </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={customIntroMessage}
                                        onChange={(e) => handleUpdate('customIntroMessage', e.target.checked, true)}
                                    />
                                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                                </label>
                            </div>

                            <div className={`transition-all duration-300 ${customIntroMessage ? 'opacity-100' : 'opacity-40 pointer-events-none grayscale'}`}>
                                <textarea
                                    value={introMessageText}
                                    onChange={(e) => handleUpdate('introMessageText', e.target.value)}
                                    onBlur={(e) => handleUpdate('introMessageText', e.target.value, true)}
                                    disabled={!customIntroMessage}
                                    className="w-full px-4 py-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all placeholder:text-slate-400 text-slate-800 min-h-[100px]"
                                    placeholder="Hello! How can I help you today?"
                                />
                                {!customIntroMessage && (
                                    <div className="mt-2 text-xs text-slate-500 flex items-center gap-1.5 lead-relaxed">
                                        <Info size={12} />
                                        Enable the toggle to customize this message.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Language Section */}
                        <div className="flex flex-col sm:flex-row sm:items-start gap-8 max-w-3xl pt-2">
                            <div className="flex-1 space-y-1">
                                <h4 className="text-sm font-semibold text-slate-900">Primary Language</h4>
                                <p className="text-xs text-slate-500 leading-relaxed">
                                    The main language your AI uses to converse.
                                </p>
                            </div>
                            <div className="w-full sm:w-64 relative">
                                <select
                                    value={defaultLanguage}
                                    onChange={(e) => handleUpdate('defaultLanguage', e.target.value, true)}
                                    className="w-full px-4 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-0 transition-all text-slate-800 appearance-none cursor-pointer"
                                >
                                    {['auto', 'en', 'es', 'fr', 'de', 'zh'].map((lang) => (
                                        <option key={lang} value={lang}>
                                            {getLanguageName(lang)}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                                    <Globe size={14} />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default Identity;
